import { useEffect, useRef, useState } from 'react';
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import { TutorialPage, useTutorialStore } from 'store/tutorialStore';
import { IntroJs } from 'intro.js/src/intro';
import Cookies from 'js-cookie';
import { useIsMobile } from 'hooks/useIsMobile';

interface TutorialProps {
  steps: any[];
  onComplete?: () => void;
  waitForSelector?: string;
  page: TutorialPage;
}

const requiredPages: TutorialPage[] = [
  'dashboard',
  'claims',
  'reports',
  'statements',
  'transactions',
  'settings',
];

const allPagesVisited = (visited: Set<string>) =>
  requiredPages.every((page) => visited.has(page));

export const TutorialComponent: React.FC<TutorialProps> = ({
  steps,
  onComplete,
  waitForSelector,
  page,
}) => {
  const {
    startTutorial,
    markPageVisited,
    visitedPages,
    completeTutorial,
    isTutorialActive,
  } = useTutorialStore();

  const isMobile = useIsMobile(700);

  useEffect(() => {
    if (page) {
      markPageVisited(page);
    }
  }, [page, markPageVisited]);

  useEffect(() => {
    if (allPagesVisited(visitedPages)) {
      Cookies.set('tutorialCompleted', 'true');
    }
  }, [visitedPages]);

  const initialShouldShow =
    isTutorialActive &&
    Cookies.get('tutorialCompleted') !== 'true' &&
    !isMobile;
  !allPagesVisited(visitedPages);

  const [shouldShow, setShouldShow] = useState(initialShouldShow);
  const [isReady, setIsReady] = useState(waitForSelector ? false : true);
  const introInstance = useRef<IntroJs | null>(null);
  const hasFinished = useRef(false);

  useEffect(() => {
    if (waitForSelector) {
      const interval = setInterval(() => {
        if (document.querySelector(waitForSelector)) {
          setIsReady(true);
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [waitForSelector]);

  // Once ready, start the intro tour.
  useEffect(() => {
    if (!shouldShow) {
      return undefined;
    }

    if (isReady && !introInstance.current) {
      introInstance.current = introJs();

      introInstance.current.setOptions({
        steps,
        exitOnOverlayClick: true,
        keyboardNavigation: true,
      });
      const finishTutorial = () => {
        if (!hasFinished.current && allPagesVisited(visitedPages)) {
          hasFinished.current = true;
          completeTutorial();
          onComplete && onComplete();
        }
      };

      // Set the skip button to close the tutorial
      // this is a workaround to replace the internal skip button and add custom logic to quit the tutorial

      setTimeout(() => {
        const skipButton = document.querySelector('.introjs-skipbutton');
        if (skipButton) {
          skipButton.replaceWith(skipButton.cloneNode(true));
          const newSkipButton = document.querySelector(
            '.introjs-skipbutton'
          ) as HTMLElement;
          newSkipButton.title = 'Close Tutorial';
          newSkipButton.textContent = '❌';
          newSkipButton.style.fontSize = '16px';
          newSkipButton.addEventListener('click', () => {
            completeTutorial();
            if (introInstance.current) {
              introInstance.current.exit(true);
            }
          });
        }
      }, 0);

      introInstance.current.oncomplete(finishTutorial);
      introInstance.current.onexit(finishTutorial);

      introInstance.current.onafterchange(async function () {
        const currentStepIndex = this._currentStep;
        const currentStep = steps[currentStepIndex];

        if (currentStep && currentStep.title === 'Expand/Collapse Row') {
          if (!document.querySelector('.claim-detail-panel')) {
            const expandButton = document.querySelector(
              '.mrt-expand-button'
            ) as HTMLElement;
            if (expandButton) {
              expandButton.click();
            }
          }
        }
        if (currentStep && currentStep.title === 'Download Statement') {
          const downloadButton = document.querySelector(
            '[data-testid="report-download-button"]'
          ) as HTMLElement;
          if (downloadButton) {
            await this.refresh();
          }
        }
        if (currentStep.title === 'Submissions') {
          const submissionsButton = document.querySelector(
            '#claim-detail-panel-segmented-control > div:nth-child(3) > label'
          ) as HTMLElement;
          if (submissionsButton) {
            submissionsButton.click();
            this.nextStep();
          }
        }
        if (currentStep.title === 'Debits') {
          const debitsButton = document.querySelector(
            '#claim-detail-panel-segmented-control > div:nth-child(4) > label'
          ) as HTMLElement;
          if (debitsButton) {
            debitsButton.click();
            this.nextStep();
          }
        }
        if (currentStep.title === 'Claims') {
          const claimsButton = document.querySelector(
            '.statement-detail-panel-segmented-control > div:nth-child(3) > label'
          ) as HTMLElement;
          if (claimsButton) {
            claimsButton.click();
            this.nextStep();
          }
        }
      });

      introInstance.current.start();
    }
    return () => {
      if (introInstance.current) {
        introInstance.current.exit(true);
        setShouldShow(false);
        introInstance.current = null;
      }
    };
  }, [isReady, steps, onComplete, startTutorial, waitForSelector]);

  return null;
};
